import React, { useEffect, useMemo, useRef } from 'react'
import {
  IconButton,
  Icon,
  Logo,
  Wrapper,
  Flex,
  Popover,
  Transition,
  Portal,
} from '@lounge-fe/ui-kit'
import { useShopify } from '../../../../../hooks/useShopify'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useScrollAnimation } from '@/features/header/hooks/useScrollAnimation'
import { useNavigationContext } from '../Navigation'
import { useSearchContext } from '@/features/navigation/components/Navigation/SearchProvider'
import { useMediaQuery } from '@lounge-fe/ui-kit/src/hooks'
import { SearchForm } from './SearchForm'
import { SearchFormMobile } from './SearchFormMobile'
import { WishlistButton } from './WishlistButton'
import { HeaderProps } from './types'
import { useAuth } from '@lounge-fe/auth'
import { useCart } from '@/features/cart/hooks'

export const HeaderComponent: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()

  const { routes } = useShopify()

  const { hasScrollAnimation, searchComponent, basketComponent } =
    useNavigationContext()
  const isDesktop = useMediaQuery(({ screens }) => `(min-width: ${screens.md})`)

  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const scrollAnimationProps = useScrollAnimation()
  const { hiddenState } = scrollAnimationProps

  const searchContext = useSearchContext()

  const isOnSearchPage = useMemo(() => {
    return window.location.pathname === '/search'
  }, [])

  useEffect(() => {
    const headerEl = document.getElementById('shopify-section-layout_header')

    if (!headerEl) return

    const isScrollDisabled = headerEl.classList.contains('scroll-disabled')

    if (!isScrollDisabled) {
      headerEl.classList.toggle('header-hidden', hiddenState)
    }
  }, [hiddenState])

  const { data: { itemCount } = {} } = useCart()

  const actionsGap = itemCount && itemCount >= 10 ? 'gap-2' : 'gap-1 sm:gap-2'

  const handleSearchButtonClick = () => {
    if (isOnSearchPage) {
      const searchInput = document.querySelector(
        '#search-products',
      ) as HTMLInputElement
      if (searchInput) {
        searchInput.focus()
      }
    }
  }

  return (
    <Popover shards={[searchInputRef]}>
      {({ isOpen }) => (
        <>
          <div className={classNames('navigation-header__wrapper')}>
            <motion.header
              className={classNames(
                'bg-primary',
                'dark:bg-inverse',
                'relative',
                'w-full',
                'top-0',
                'left-0',
                'right-0',
                'h-[calc(var(--header-height)_-_1px)]',
                isDesktop &&
                  'after:content-empty after:absolute after:left-0 after:right-0 after:h-[1px] after:z-50 after:bg-neutral-100 after:pointer-events-none',
                hiddenState && 'after:hidden',
              )}
              {...{
                ...(hasScrollAnimation ? scrollAnimationProps : {}),
              }}
            >
              <Wrapper className="h-full w-screen max-w-full px-4 lg:px-10">
                <Transition.Fade
                  as={Flex}
                  className="items-center h-full"
                  show={!isOpen}
                  initial={false}
                  onAnimationEnd={() => {
                    if (isOpen) {
                      searchInputRef.current?.focus()
                    }
                  }}
                >
                  <Flex className={classNames('items-center flex-1')}>
                    <IconButton
                      onClick={onMenuClick}
                      type="button"
                      aria-label={t('action.open')}
                      size="md"
                      icon={<Icon.BurgerMenu />}
                    />
                  </Flex>
                  <Flex
                    as="a"
                    href={routes.root_url}
                    title="Lounge Underwear"
                    className={classNames('align-center justify-center flex-1')}
                  >
                    <Logo className="navigation__logo h-4 sm:h-5" />
                  </Flex>
                  <Flex
                    className={classNames('flex-1 justify-end', actionsGap)}
                  >
                    {isOnSearchPage ? (
                      <IconButton
                        icon={<Icon.Search width={16} height={16} />}
                        aria-label={t('action.search')}
                        onClick={handleSearchButtonClick}
                      />
                    ) : (
                      <Popover.Trigger>
                        <IconButton
                          icon={<Icon.Search width={16} height={16} />}
                          aria-label={t('action.open')}
                        />
                      </Popover.Trigger>
                    )}

                    {isDesktop && (
                      <>
                        <WishlistButton />
                        <IconButton
                          type="button"
                          icon={<Icon.Account width={16} height={16} />}
                          href={
                            isAuthenticated
                              ? routes.account_url
                              : routes.account_login_url
                          }
                          aria-label={t('account.account')}
                        />
                      </>
                    )}
                    <div className="flex items-center justify-center">
                      {basketComponent}
                    </div>
                  </Flex>
                </Transition.Fade>
              </Wrapper>
            </motion.header>
          </div>
          <Portal>
            <Popover.Backdrop className="z-40" />
            <Transition.Fade
              show={isOpen}
              className="fixed w-screen top-0 left-0 right-0 z-50"
            >
              <Popover.Content
                tabIndex={-1}
                className={classNames(
                  'bg-white flex flex-col items-center overflow-y-auto max-h-screen',
                  isDesktop ? 'shadow-md' : 'h-[100dvh] inset-0',
                )}
                aria-label={t('action.search')}
              >
                {isDesktop ? (
                  <SearchForm ref={searchInputRef} />
                ) : (
                  <SearchFormMobile showClearButton focusOnMount />
                )}
                <div
                  className={classNames(
                    'w-full',
                    isDesktop ? 'h-full' : 'h-[100dvh]',
                  )}
                >
                  {typeof searchComponent === 'function'
                    ? searchComponent(searchContext)
                    : searchComponent}
                </div>
              </Popover.Content>
            </Transition.Fade>
          </Portal>
        </>
      )}
    </Popover>
  )
}
